import { DeleteOutlined } from '@ant-design/icons'
import { ProFormGroup } from '@ant-design/pro-components'
import { useAppFormContext } from '@app/icare/form/useAppForm'
import { Trans, useLingui } from '@lingui/react/macro'
import type { PreferredCommunicationType } from '@pkg/form/common'
import { FavoriteField } from '@pkg/form/components/FavoriteField'
import { useFieldContext } from '@pkg/form/createAppFormHook'
import { joinNames } from '@pkg/form/utils/form.utils'
import { stopEvent } from '@pkg/shared/utils/ui.utils'
import { useStore } from '@tanstack/react-form'
import { Button } from 'antd'
import { useCallback } from 'react'

export type ContactPerson = {
  salutation?: string | null
  title?: string | null
  firstName?: string | null
  lastName?: string | null
  contact?: {
    email?: string | null
    phone?: string | null
    fax?: string | null
    preferredCommunication?: PreferredCommunicationType | null
  } | null
}

export type ContactPersonFieldProps = {
  className?: string
  hideFavorite?: boolean
  onRemove?: () => void
}

export function ContactPersonField(props: ContactPersonFieldProps) {
  const { className, hideFavorite, onRemove } = props
  const field = useFieldContext<ContactPerson>()
  const form = useAppFormContext()

  const preferredCommunication = useStore(field.store, s => s.value?.contact?.preferredCommunication)

  const { t } = useLingui()

  const toggleFavorite = useCallback(
    (type: PreferredCommunicationType) => {
      const currentValue = field.state.value?.contact?.preferredCommunication
      const nextValue = currentValue === type ? null : type

      field.setValue(v => ({
        ...v,
        contact: {
          ...v?.contact,
          preferredCommunication: nextValue,
        },
      }))
    },
    [field],
  )

  return (
    <ProFormGroup
      title={onRemove && <Button type="text" danger icon={<DeleteOutlined />} onClick={stopEvent(onRemove)} />}
      rowProps={{ gutter: [16, 0] }}
      className={className}
    >
      <form.AppField name={joinNames(field, 'salutation')}>
        {field => (
          <field.Select
            label={t`Anrede`}
            colProps={{ md: 4, xs: 24 }}
            request={async () => [
              { label: t`Herr`, value: 'MR' },
              { label: t`Frau`, value: 'MRS' },
            ]}
          />
        )}
      </form.AppField>
      <form.AppField name={joinNames(field, 'title')}>
        {field => (
          <field.Select
            label={t`Titel`}
            colProps={{ md: 4, xs: 24 }}
            options={['', 'Dr.', 'Dr. med.', 'Dr. Dr. med.', 'Prof.', 'Prof. Dr.', 'Prof. Dr. med.']}
          />
        )}
      </form.AppField>
      <form.AppField name={joinNames(field, 'firstName')}>
        {field => <field.Text label={<Trans>Vorname</Trans>} colProps={{ md: 8, xs: 24 }} />}
      </form.AppField>
      <form.AppField name={joinNames(field, 'lastName')}>
        {field => <field.Text label={<Trans>Nachname</Trans>} colProps={{ md: 8, xs: 24 }} />}
      </form.AppField>

      <form.AppField name={joinNames(field, 'contact_email')}>
        {field => (
          <field.Text
            label={t`E-Mail`}
            colProps={{ md: 8, xs: 24 }}
            fieldProps={{
              suffix: !hideFavorite && (
                <FavoriteField type="email" value={preferredCommunication} onChange={toggleFavorite} />
              ),
            }}
          />
        )}
      </form.AppField>
      <form.AppField name={joinNames(field, 'contact.phone')}>
        {field => (
          <field.Text
            label={t`Telefon`}
            colProps={{ md: 8, xs: 24 }}
            fieldProps={{
              suffix: !hideFavorite && (
                <FavoriteField type="phone" value={preferredCommunication} onChange={toggleFavorite} />
              ),
            }}
          />
        )}
      </form.AppField>
      <form.AppField name={joinNames(field, 'contact.fax')}>
        {field => (
          <field.Text
            label={t`Fax`}
            colProps={{ md: 8, xs: 24 }}
            fieldProps={{
              suffix: !hideFavorite && (
                <FavoriteField type="fax" value={preferredCommunication} onChange={toggleFavorite} />
              ),
            }}
          />
        )}
      </form.AppField>
    </ProFormGroup>
  )
}
